//src/hooks/useDepartments.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createDepartment,
  getDepartment,
  listDepartments,
  setDepartmentActive,
  updateDepartment,
  type DepartmentUpsertIn,
} from "@/services/departments";

export function useDepartments(includeInactive = false) {
  return useQuery({
    queryKey: ["departments", { includeInactive }],
    queryFn: () => listDepartments(includeInactive),
    staleTime: 30_000,
  });
}

export function useDepartmentDetail(id?: string) {
  return useQuery({
    queryKey: ["department", id],
    queryFn: () => getDepartment(id!),
    enabled: !!id,
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DepartmentUpsertIn) => createDepartment(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DepartmentUpsertIn }) =>
      updateDepartment(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["department", variables.id] });
    },
  });
}

export function useSetDepartmentActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setDepartmentActive(id, is_active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["department", variables.id] });
    },
  });
}
